import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { Mail, KeyRound } from "lucide-react";

import { Button, TextField } from "../../../ui/index.ts";
import {
  forgotPasswordSchema,
  otpVerifySchema,
  type ForgotPasswordValues,
  type OtpVerifyValues,
} from "../schemas/index.ts";
import { useForgotPassword } from "../hooks/use-forgot-password.ts";
import { formatFormFieldErrors } from "./form-errors.ts";
import styles from "./login-form.module.css"; // Reuse login form styles for consistency

export function ForgotPasswordForm({ emailPlaceholder = "you@caeliusconsulting.com" }: { emailPlaceholder?: string }) {
  const {
    loading,
    error,
    isOTPSent,
    timeLeft,
    isResendActive,
    sendOTP,
    verifyOTP,
    resendOTP,
  } = useForgotPassword();

  const [email, setEmail] = useState("");

  const emailForm = useForm({
    defaultValues: { email: "" } as ForgotPasswordValues,
    validators: { onSubmit: forgotPasswordSchema },
    onSubmit: async ({ value }) => {
      setEmail(value.email);
      await sendOTP(value.email);
    },
  });

  const otpForm = useForm({
    defaultValues: { otp: "" } as OtpVerifyValues,
    validators: { onSubmit: otpVerifySchema },
    onSubmit: async ({ value }) => {
      await verifyOTP(value.otp, email);
    },
  });

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (isOTPSent) {
    return (
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void otpForm.handleSubmit();
        }}
        noValidate
      >
        {error != null ? (
          <p className={`${styles.errorBanner} body-sm`} role="alert">
            {error}
          </p>
        ) : null}

        <otpForm.Field name="otp">
          {(field) => (
            <div className={styles.fieldWrapper}>
              <span className={styles.fieldLeadIcon} aria-hidden="true">
                <KeyRound size={18} />
              </span>
              <TextField
                label="Verification Code (OTP)"
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={formatFormFieldErrors(field.state.meta.errors)}
                disabled={loading}
                maxLength={6}
                className={styles.inputElevated}
              />
            </div>
          )}
        </otpForm.Field>

        <div className={styles.actions}>
          <Button
            type="submit"
            fullWidth
            disabled={loading}
            className={styles.submitButton}
            startIcon={
              loading ? <span className={styles.spinner} aria-hidden="true" /> : undefined
            }
          >
            {loading ? "Verifying\u2026" : "Verify OTP"}
          </Button>

          <div style={{ textAlign: "center", marginTop: "var(--spacing-sm)" }}>
            {isResendActive ? (
              <button
                type="button"
                onClick={() => resendOTP(email)}
                className={`${styles.forgotLink} body-sm`}
                style={{ border: "none", background: "none", cursor: "pointer" }}
                disabled={loading}
              >
                Resend the OTP
              </button>
            ) : (
              <p className="body-sm" style={{ color: "var(--content-muted)" }}>
                Resend in <span style={{ color: "var(--content-accent)", fontWeight: "600" }}>{formatTime(timeLeft)}</span>
              </p>
            )}
          </div>

          <Link to="/" className={`${styles.forgotLink} body-sm`}>
             Back to Login
          </Link>
        </div>
      </form>
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void emailForm.handleSubmit();
      }}
      noValidate
    >
      {error != null ? (
        <p className={`${styles.errorBanner} body-sm`} role="alert">
          {error}
        </p>
      ) : null}

      <emailForm.Field name="email">
        {(field) => (
          <div className={styles.fieldWrapper}>
            <span className={styles.fieldLeadIcon} aria-hidden="true">
              <Mail size={18} />
            </span>
            <TextField
              label="Email address"
              id="forgot-email"
              type="email"
              autoComplete="email"
              placeholder={emailPlaceholder}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={loading}
              maxLength={100}
              className={styles.inputElevated}
            />
          </div>
        )}
      </emailForm.Field>

      <div className={styles.actions}>
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          className={styles.submitButton}
          startIcon={
            loading ? <span className={styles.spinner} aria-hidden="true" /> : undefined
          }
        >
          {loading ? "Sending OTP\u2026" : "Get Verification Code"}
        </Button>

        <Link to="/" className={`${styles.forgotLink} body-sm`}>
           Back to Login
        </Link>
      </div>
    </form>
  );
}
